import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, Loader2, AlertCircle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-md bg-sky-600 flex items-center justify-center">
            <Truck className="h-6 w-6" />
          </div>
          <div>
            <div className="text-lg font-head font-bold tracking-tight">TMS</div>
            <div className="text-xs text-slate-400">Sistema Gestione Trasporti</div>
          </div>
        </div>
        <div className="max-w-md">
          <h1 className="text-3xl font-head font-bold leading-tight">
            Gestione flotta, autisti e scadenze in un unico strumento.
          </h1>
          <p className="mt-4 text-slate-400 text-sm leading-relaxed">
            Anagrafiche, scadenzari mezzi e conducenti, controllo operativo e
            pre-fatturazione. Sostituisce i fogli Excel con dati centralizzati e
            tracciati.
          </p>
        </div>
        <div className="text-xs text-slate-500">
          Strumento interno · Accesso riservato al personale autorizzato
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-md bg-sky-600 flex items-center justify-center">
              <Truck className="h-5 w-5 text-white" />
            </div>
            <div className="text-lg font-head font-bold">TMS</div>
          </div>
          <h2 className="text-2xl font-head font-bold text-slate-900">Accedi</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">
            Inserisci le tue credenziali per continuare.
          </p>

          {error && (
            <div
              className="mb-4 flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
              data-testid="login-error"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-1.5"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Accedi
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
